import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation } from '@apollo/client';
import {
  AuthQueryName,
  QuestionnaireQueryName,
  SAVE_QUESTIONNAIRE_BATCH_MUTATION,
  SAVE_QUESTIONNAIRE_MUTATION,
} from '../../gql';
import { QUESTIONNAIRE_BUNDLE_SIZE } from '../../config';
import {
  footerStyles,
  headerStyles,
  mediumBMargin,
  mediumBPadding,
  mediumTMargin,
  paperStyles,
  progressBarStyles,
  radioGroupStyles,
} from './styles';
import { QuestionnaireDone } from './QuestionnaireDone';
import { FormattedMessage } from 'react-intl';

type QuestionnaireItem = {
  id: number;
  item: string;
  value?: number;
};

interface QuestionnaireProps {
  completeBundle: number[];
  onFinish: () => void;
  questionnaire: QuestionnaireItem[];
}

const SCALE = [1, 2, 3, 4, 5] as const;

const getPageNumber = (
  completeBundle: number[],
  batch: QuestionnaireItem[]
) => {
  if (batch.length === 0) {
    return 0;
  }

  const lastOrdinal = completeBundle.indexOf(batch[batch.length - 1].id) + 1;
  return lastOrdinal / QUESTIONNAIRE_BUNDLE_SIZE;
};

const Questionnaire = ({
  completeBundle,
  onFinish,
  questionnaire,
}: QuestionnaireProps) => {
  const [answers, setAnswers] = React.useState<Record<number, number>>(
    questionnaire.reduce(
      (acc, q) => (q.value ? { ...acc, [q.id]: q.value } : acc),
      {}
    )
  );
  const [showValidation, setShowValidation] = React.useState(false);
  const [allIpipDone, setAllIpipDone] = React.useState(false);

  const answeredCount = questionnaire.filter(
    (q) => answers[q.id] != null
  ).length;
  const isComplete = answeredCount === questionnaire.length;
  const pageNum = getPageNumber(completeBundle, questionnaire);
  const pages = completeBundle.length / QUESTIONNAIRE_BUNDLE_SIZE;
  const percentDone = allIpipDone
    ? 100
    : Math.round(((pageNum - 1) / pages) * 100);

  const [saveQuestionnaireAnswer, { loading: isSaveAnswerLoading }] =
    useMutation(SAVE_QUESTIONNAIRE_MUTATION);
  const [saveQuestionnaireBatch, { loading: isSubmitLoading }] = useMutation(
    SAVE_QUESTIONNAIRE_BATCH_MUTATION
  );

  const setAnswer = async (id: number, value: number) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setShowValidation(false);
    await saveQuestionnaireAnswer({
      variables: {
        questionId: id,
        value,
      },
    });
  };

  const handleNext = async () => {
    if (!isComplete) {
      setShowValidation(true);
      return;
    }

    await saveQuestionnaireBatch({
      variables: {
        items: questionnaire.map((q) => ({
          questionId: q.id,
          value: answers[q.id],
        })),
      },
      refetchQueries:
        pageNum !== pages ? [QuestionnaireQueryName] : [AuthQueryName],
    });

    if (pageNum === pages) {
      setAllIpipDone(true);
    }
  };

  if (allIpipDone || (questionnaire.length === 0 && pageNum === 0)) {
    return (
      <QuestionnaireDone onFinish={onFinish} pageNum={pageNum} pages={pages} />
    );
  }

  return (
    <Box sx={mediumBPadding}>
      <Box sx={headerStyles}>
        <Typography variant="h6" mb={1} color="#FFFFFF">
          <FormattedMessage
            id="app.questionnaire.questionnaire"
            defaultMessage="Questionnaire"
            description="Questionnaire"
          />
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
          my={2}
        >
          <Stack direction="column" gap={2} mb={1}>
            {pageNum === 1 ? (
              <>
                <Typography component="p" variant="body2" color="#FFFFFF">
                  <FormattedMessage
                    id="app.questionnaire.header.pause"
                    defaultMessage="Now there will be a short break. But don’t worry—after completing this short personality questionnaire, a few more guessing questions will be waiting for you."
                    description="Short pause"
                  />
                </Typography>
                <Typography component="p" variant="body2" color="#FFFFFF">
                  <FormattedMessage
                    id="app.questionnaire.header.explanation"
                    defaultMessage="Below are statements describing different ways of thinking, feeling, and behaving. For each statement, indicate to what extent it generally applies to you. This is not about how you feel right now, but what you are usually like."
                    description="Explanation"
                  />
                </Typography>
              </>
            ) : (
              <Typography component="p" variant="body2" color="#FFFFFF">
                <FormattedMessage
                  id="app.questionnaire.header.instruction"
                  defaultMessage="Indicate to what extent each statement generally applies to you."
                  description="Instruction"
                />
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="#FFFFFF" mb={1} flexShrink={0}>
            <FormattedMessage
              id="app.questionnaire.page"
              defaultMessage="Page"
              description="Page"
            />{' '}
            <b>{pageNum}</b> / <b>{pages}</b>
          </Typography>
        </Stack>

        {showValidation && (
          <Alert severity="warning" sx={mediumBMargin}>
            <FormattedMessage
              id="app.questionnaire.warning"
              defaultMessage="Please fill in all answers before you continue."
              description="Instruction"
            />
          </Alert>
        )}

        <LinearProgress
          variant="determinate"
          value={percentDone}
          sx={progressBarStyles}
        />
      </Box>
      <Paper variant="outlined" sx={paperStyles}>
        <Stack spacing={2}>
          {questionnaire.map((q, idx) => {
            const value = answers[q.id] ?? '';

            return (
              <Box key={q.id}>
                <Typography variant="body2" mb={1}>
                  {idx + 1 + (pageNum - 1) * 10}. {q.item}
                </Typography>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="secondary">
                    <FormattedMessage
                      id="app.questionnaire.completelyinnacurate"
                      defaultMessage="Completely Inaccurate"
                      description="Completely Innacurate"
                    />
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    <FormattedMessage
                      id="app.questionnaire.completelyacurate"
                      defaultMessage="Completely Accurate"
                      description="Completely Acurate"
                    />{' '}
                  </Typography>
                </Stack>
                <RadioGroup
                  row
                  value={value}
                  onChange={(e) => setAnswer(q.id, Number(e.target.value))}
                  sx={radioGroupStyles}
                >
                  {SCALE.map((v) => (
                    <FormControlLabel
                      key={v}
                      value={v}
                      control={<Radio />}
                      label={String(v)}
                      labelPlacement="bottom"
                    />
                  ))}
                </RadioGroup>

                {idx < questionnaire.length - 1 ? (
                  <Divider sx={mediumTMargin} />
                ) : null}
              </Box>
            );
          })}
        </Stack>
      </Paper>

      <Box sx={footerStyles}>
        <Button
          variant="contained"
          disabled={!isComplete || isSaveAnswerLoading || isSubmitLoading}
          onClick={handleNext}
        >
          <FormattedMessage
            id="app.questionnaire.send"
            defaultMessage="Send"
            description="Send"
          />
        </Button>
      </Box>
    </Box>
  );
};

export { Questionnaire };
